// Performance measurement (docs/PERFORMANCE.md tooling): Web Vitals via native
// PerformanceObserver, long-task attribution, all reported through the service.

import { track } from "./service";

let lcp = 0;
let cls = 0;
let worstInp = 0;

export function initPerfObservers(routeName: () => string): void {
  try {
    new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1);
      if (last) lcp = last.startTime;
    }).observe({ type: "largest-contentful-paint", buffered: true });

    new PerformanceObserver((list) => {
      for (const e of list.getEntries() as PerformanceEntry[]) {
        const shift = e as unknown as { value: number; hadRecentInput: boolean };
        if (!shift.hadRecentInput) cls += shift.value;
      }
    }).observe({ type: "layout-shift", buffered: true });

    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const ev = e as PerformanceEventTiming;
        if (ev.duration > worstInp) worstInp = ev.duration;
      }
    }).observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

    // Main-thread blocks over budget during interaction (budget: 100 ms).
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration > 100) {
          track("perf.block", { ms: Math.round(e.duration), surface: routeName() });
        }
      }
    }).observe({ type: "longtask", buffered: true });
  } catch {
    // Older engines without these entry types: vitals simply absent.
  }

  // Report once the page is hidden (final values).
  const report = () => {
    track("perf.vitals", {
      lcp: Math.round(lcp),
      cls: Math.round(cls * 1000) / 1000,
      inp: Math.round(worstInp),
      route: routeName(),
    });
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") report();
  });
}

/** Dev-mode frame monitor: sustained fps + long-frame counts for a surface. */
export function frameMonitor(surface: string, sampleSeconds = 5): () => void {
  let frames = 0;
  let longFrames = 0;
  let last = performance.now();
  let running = true;

  const loop = (now: number) => {
    if (!running) return;
    frames += 1;
    if (now - last > 33) longFrames += 1;
    last = now;
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  const timer = setInterval(() => {
    const fps = Math.round(frames / sampleSeconds);
    if (import.meta.env.DEV) console.debug(`%c▸ ${surface} ${fps}fps (${longFrames} long)`, "color:#7FA8FF");
    track("perf.frame", { fps, longFrames, surface });
    frames = 0;
    longFrames = 0;
  }, sampleSeconds * 1000);

  return () => {
    running = false;
    clearInterval(timer);
  };
}
